import { useState } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Lock, ShieldCheck } from 'lucide-react';
import { AuthThemeToggle } from '../../components/AuthThemeToggle';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Logo } from '../../components/Logo';
import { useBrand } from '../../brand/BrandProvider';

/**
 * Password reset confirm — §7.1
 * User clicks email link → lands here with a token → sets new password → redirects to /login.
 */
export function ResetPassword() {
  const brand = useBrand();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;
  const valid = !tooShort && !mismatch && password.length >= 8;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    setTimeout(() => {
      navigate({ to: '/login' });
    }, 900);
  }

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark grid lg:grid-cols-2">
      {/* Left — pitch */}
      <div className="hidden lg:flex relative overflow-hidden p-12 xl:p-20 flex-col justify-between border-r border-zinc-200 dark:border-zinc-700">
        <div className="glow-bg absolute inset-0 pointer-events-none" aria-hidden="true" />
        <div className="relative">
          <div className="flex items-center gap-2.5">
            <Logo />
            <span className="font-semibold text-lg tracking-tight text-zinc-950 dark:text-zinc-50">
              {brand.name}
            </span>
          </div>
        </div>
        <div className="relative">
          <h1 className="text-4xl xl:text-5xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-[1.05] max-w-lg">
            New password,
            <br />
            <span className="text-zinc-500 dark:text-zinc-500">fresh start.</span>
          </h1>
          <p className="mt-6 text-base text-zinc-600 dark:text-zinc-300 max-w-md">
            Pick a strong one this time — at least 8 characters, with letters and numbers.
            We'll log you in automatically once it's saved.
          </p>
        </div>
        <div className="relative text-xs text-zinc-500 dark:text-zinc-400">
          © 2026 ITCity Ltd. · Sylhet, Bangladesh
        </div>
      </div>

      {/* Right — form */}
      <div className="relative flex flex-col justify-center p-6 sm:p-12">
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
          <AuthThemeToggle />
        </div>
        <div className="lg:hidden flex items-center gap-2.5 mb-12">
          <Logo />
          <span className="font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            {brand.name}
          </span>
        </div>

        <div className="w-full max-w-sm mx-auto">
          <h2 className="text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Choose a new password
          </h2>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            Make it long, memorable, and not used anywhere else.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <Input
              label="New password"
              type="password"
              required
              placeholder="••••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hint="At least 8 characters with letters and numbers"
              error={tooShort ? 'Must be at least 8 characters' : undefined}
              autoComplete="new-password"
            />
            <Input
              label="Confirm password"
              type="password"
              required
              placeholder="••••••••••"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              error={mismatch ? "Passwords don't match" : undefined}
              autoComplete="new-password"
            />

            <Button
              type="submit"
              fullWidth
              disabled={!valid}
              loading={submitting}
              iconRight={!submitting ? <ArrowRight className="w-4 h-4" /> : undefined}
            >
              {submitting ? 'Saving…' : 'Save & sign in'}
            </Button>

            <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
              <ShieldCheck className="w-3 h-3" />
              <span>This signs out other active sessions</span>
            </div>
          </form>

          <p className="mt-10 text-sm text-zinc-600 dark:text-zinc-300 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 font-medium text-brand dark:text-brand-dm hover:underline"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
